import React from 'react';
import { Box, IconButton, Typography, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  topNav: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2),
    flexWrap: 'nowrap',
    overflow: 'hidden',
    minHeight: 48,
  },
  divider: {
    width: 1,
    height: 24,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 1),
  },
  tabs: {
    flex: 1,
    minHeight: 48,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-root': {
      minHeight: 48,
    },
    '& .MuiTab-root': {
      minHeight: 48,
      padding: theme.spacing(0, 2),
    },
  },
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  subNav: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 2),
    minHeight: 40,
  },
}));

export const AdminAIDashboard: React.FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab = location.pathname.split('/')[2] || 'ai-comments';
  const processingSubTab = location.pathname.split('/')[3] || 'batches';

  const handleTabChange = (_: React.SyntheticEvent, next: string) => {
    if (next) {
      if (next === 'processing') {
        navigate('/admin/processing/batches');
      } else {
        navigate(`/admin/${next}`);
      }
    }
  };

  const handleProcessingSubTabChange = (
    _: React.SyntheticEvent,
    next: string,
  ) => {
    if (next) navigate(`/admin/processing/${next}`);
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.topNav}>
          <IconButton
            onClick={handleBack}
            size="small"
            title="Back to application"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            sx={{ fontSize: '1rem', fontWeight: 600 }}
          >
            AI Admin
          </Typography>
          <Box className={classes.divider} />
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            className={classes.tabs}
          >
            <Tab
              value="ai-comments"
              label={
                <Box className={classes.tabLabel}>Comments AI (legacy)</Box>
              }
            />
            <Tab
              value="ai-chat-conversations"
              label={<Box className={classes.tabLabel}>Conversations</Box>}
            />
            <Tab
              value="ai-chat-tools"
              label={<Box className={classes.tabLabel}>Tools</Box>}
            />
            <Tab
              value="ai-models"
              label={<Box className={classes.tabLabel}>Models</Box>}
            />
            <Tab
              value="processing"
              label={<Box className={classes.tabLabel}>AI File Imports</Box>}
            />
          </Tabs>
        </Box>
        {currentTab === 'processing' && (
          <Box className={classes.subNav}>
            <Tabs
              value={processingSubTab}
              onChange={handleProcessingSubTabChange}
              variant="scrollable"
              scrollButtons={false}
              className={classes.tabs}
            >
              <Tab
                value="batches"
                label={<Box className={classes.tabLabel}>Test Importer</Box>}
              />
              <Tab
                value="flowchart"
                label={<Box className={classes.tabLabel}>Flowchart</Box>}
              />
              <Tab
                value="pipeline"
                label={<Box className={classes.tabLabel}>Prompt Reference</Box>}
              />
            </Tabs>
          </Box>
        )}
      </Box>
      <Box className={classes.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
